import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  FilterTreeJSONCollection,
  FilterTreeActiveMap,
  FilterTreeJSONItem,
  FilterTreeMap
} from '../../services/modal/modals/filters/filters.interface';
import { take } from 'rxjs/operators';
import { ModalService } from '../../services/modal/modal.service';
import { ProjectElement } from '../../resources/element/project-element.interface';
import { FILTER_PROPERTY_BY } from './table-filters.map';

/**
 * Core Table Filters service.
 *
 * Handles all *logic* for the table filters.
 *
 * This Service should **never** be included directly into Service other
 * than TableFilterService.
 */
@Injectable({
  providedIn: 'root'
})
export class TableFiltersCoreService {

  constructor(
    protected modalService: ModalService
  ) {
    this._trackFiltersMap();
  }

  /** Copy of the filters stored on init, used for resetting to default.  */
  private _filters = {};

  /** Filters active Observable */
  public active$ = new BehaviorSubject<boolean>(false);

  /** Filters active Observable */
  public filter$ = new BehaviorSubject<FilterTreeJSONCollection>(null);

  /** Filters active Subject. */
  public filtersMap$ = new BehaviorSubject<FilterTreeActiveMap>({});

  /**
   * Clear all filters and state, should be called in `ngOnDestroy`.
   *
   * @todo Create method. This is not needed for now, simply calling onInit
   *  again with new values, works fine. However when the filters will be
   *  used on multiple pages, it's generaly a good idea to to a cleanup.
   */
  public clear(): void { }

  /**
   * Apply filters to element collection.
   *
   * @param elements The list of elements to filters.
   * @returns List of filtered elements.
   */
  public filter(elements: ProjectElement[]): ProjectElement[] {
    const filterMap = this.generateFilterMap(this.filter$.getValue());
    for (const filter of Object.entries(filterMap)) {

      // Filter by value (default)
      if (FILTER_PROPERTY_BY[filter[0]] === 'value' || !FILTER_PROPERTY_BY[filter[0]]) {
        elements = elements.filter(e => {
          return (filter[1] as string[]).indexOf(e[filter[0]]) === -1;
        });
      }

      // Filter by length
      if (FILTER_PROPERTY_BY[filter[0]] === 'length') {
        elements = elements.filter(e => {
          return (filter[1] as string[]).indexOf(e[filter[0]].length) === -1;
        });
      }
    }
    return elements;
  }

  /**
   * Parse JSON tree to iterable filter data.
   *
   * example:
   * {
   *   constructionId: ['5e382ac4d50a8b4a9721cc15', '5e382ac4d50a8b4a9721cc02'],
   *   moduleId: ['5e382ac4d50a8b4a9721cc15', '5e382ac4d50a8b4a9721cc02'],
   *   ...
   * }
   *
   * @returns Object with filters.
   */
  public generateFilterMap(filtersJSON: FilterTreeJSONCollection): FilterTreeMap {
    const filters = {};
    for (const collection of Object.entries(filtersJSON)) {
      for (const column of collection[1] as FilterTreeJSONItem[]) {
        for (const row of column.children) {
          if (row.active === false) {
            if (!filters[row.key]) {
              filters[row.key] = [];
            }
            filters[row.key].push(row.value);
          }
        }
      }
    }
    return filters;
  }

  /**
   * Generate simple lookup table to quickly see which columns have an
   * active filter.
   *
   * @param filters JSON filters tree.
   * @returns Lookup table.
   */
  public generateActiveMap(filtersJSON: FilterTreeJSONCollection): FilterTreeActiveMap {
    const map = {};
    for (const column of Object.entries(filtersJSON)) {
      let isActive = false;
      for (const items of column[1]) {
        if (items.children.filter(i => !i.active).length > 0) {
          isActive = true;
        }
      }
      map[column[0]] = isActive;
    }
    return map;
  }

  /**
   * Initialize filters.
   *
   * Example:
   * {
   *   'rowName: [
   *     this.service.getProjects(),
   *     this.service.getConstructions(),
   *   ],
   *   ...
   * }
   *
   * @param [filters=this._filters] Constructed set of filters.
   */
  public initFilters(filters: any = this._filters): void {
    this._filters = JSON.parse(JSON.stringify(filters));
    this.filter$.next(filters);
  }

  /**
   * Reset all filters to initial state.
   */
  public reset(): void {
    this.initFilters(this._filters);
    this.filter$.pipe(take(1)).subscribe(f => {
      this.filtersMap$.next(this.generateActiveMap(f));
    });
  }

  /**
   * Set filters.
   *
   * @todo Rename to showFiltersFor, as you are not setting, just opening the modal.
   *
   * @param row The filter row to update.
   */
  public showFiltersFor(row: 'chapter' | 'construction' | 'status' | 'user'): void {
    const filters = this.filter$.getValue();
    this.modalService.openFilters(filters[row]).afterClosed().subscribe(data => {
      if (data) {

        // Update filters data
        filters[row] = data;
        this.filter$.next(filters);
        this.filtersMap$.next(this.generateActiveMap(filters));
      }
    });
  }

  /**
   * Track filtersMap.
   *
   * Used for live updating the active state.
   */
  private _trackFiltersMap(): void {
    this.filtersMap$.subscribe(fm => {
      this.active$.next(
        Object.entries(this.filtersMap$.getValue()).filter(filter => filter[1]).length > 0
      );
    });
  }
}
